import { Link } from "react-router-dom";
import ArticleFAQ from "./ArticleFAQ";

interface FAQItem {
  question: string;
  answer: string;
}

interface ArticleBodyProps {
  content: string;
  tags: string[];
  faq?: FAQItem[];
}

export default function ArticleBody({ content, tags, faq }: ArticleBodyProps) {
  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-0 pb-16">
      {/* Prose content */}
      <div
        className="
          mt-10
          [&_p]:text-[#3A506B] [&_p]:leading-[1.85] [&_p]:text-base [&_p]:mb-6
          [&_h2]:text-2xl [&_h2]:font-extrabold [&_h2]:text-[#222831] [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:leading-snug
          [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-[#222831] [&_h3]:mt-8 [&_h3]:mb-3
          [&_ul]:mb-6 [&_ul]:flex [&_ul]:flex-col [&_ul]:gap-3
          [&_li]:flex [&_li]:items-start [&_li]:gap-3 [&_li]:text-sm [&_li]:text-[#3A506B] [&_li]:leading-relaxed
          [&_li]:before:content-[''] [&_li]:before:w-1.5 [&_li]:before:h-1.5 [&_li]:before:rounded-full [&_li]:before:bg-[#C9A227] [&_li]:before:flex-shrink-0 [&_li]:before:mt-2
          [&_strong]:font-semibold [&_strong]:text-[#222831]
          [&_blockquote]:border-l-4 [&_blockquote]:border-[#C9A227] [&_blockquote]:bg-[#C9A227]/5 [&_blockquote]:pl-6 [&_blockquote]:pr-4 [&_blockquote]:py-5 [&_blockquote]:my-10 [&_blockquote]:rounded-r-xl
          [&_blockquote_p]:text-base [&_blockquote_p]:italic [&_blockquote_p]:text-[#222831] [&_blockquote_p]:font-medium [&_blockquote_p]:leading-relaxed [&_blockquote_p]:mb-0
          [&_a]:text-[#C9A227] [&_a]:font-semibold [&_a]:underline [&_a]:underline-offset-2 [&_a]:decoration-[#C9A227]/40 [&_a:hover]:text-[#b08f1e] [&_a:hover]:decoration-[#b08f1e] [&_a]:transition-colors
        "
        dangerouslySetInnerHTML={{ __html: content }}
      />

      {/* People Also Ask FAQ */}
      {faq && faq.length > 0 && <ArticleFAQ faq={faq} />}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="mt-12 pt-8 border-t border-[#E8ECF0]">
          <p className="text-xs font-bold uppercase tracking-widest text-[#3A506B]/50 mb-3">Tagged</p>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-[#F0F2F5] text-xs font-semibold text-[#3A506B]"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* About LTF Sourcing strip */}
      <div className="mt-10 rounded-2xl border border-[#E8ECF0] bg-[#FAFAFA] p-6 flex items-start gap-5">
        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#C9A227] flex-shrink-0">
          <i className="ri-links-line text-white text-lg"></i>
        </div>
        <div>
          <p className="text-sm font-bold text-[#222831] mb-1">
            Published by{" "}
            <Link to="/" className="text-[#C9A227] hover:text-[#b08f1e] transition-colors cursor-pointer">
              LTF Sourcing
            </Link>
          </p>
          <p className="text-sm text-[#3A506B]/70 leading-relaxed">
            LTF Sourcing is a specialist procurement firm helping 500+ businesses worldwide with product sourcing, wholesale supply, overstock clearance, and supply chain solutions.{" "}
            <Link to="/#services" className="text-[#C9A227] font-semibold hover:text-[#b08f1e] transition-colors cursor-pointer">
              Explore our services &rarr;
            </Link>
          </p>
        </div>
      </div>

      {/* CTA Banner */}
      <div className="mt-8 rounded-2xl bg-[#222831] p-8 lg:p-10 relative overflow-hidden">
        {/* Subtle background accent */}
        <div className="absolute top-0 right-0 w-40 h-40 bg-[#C9A227]/10 rounded-full -translate-y-1/2 translate-x-1/2 pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-[#C9A227]">
              <i className="ri-links-line text-white text-xs"></i>
            </div>
            <p className="text-[#C9A227] text-xs font-bold uppercase tracking-widest">LTF Sourcing</p>
          </div>
          <h3 className="text-2xl font-extrabold text-white mb-3">
            Ready to Apply This? Talk to a Specialist.
          </h3>
          <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-md">
            Our team handles product sourcing, wholesale supply, and inventory solutions for 500+ businesses globally. Get a free, no-obligation consultation.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#C9A227] text-white text-sm font-bold hover:bg-[#b08f1e] transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-chat-1-line text-base"></i>
              Get a Free Quote
            </a>
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/60 hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              Visit LTF Sourcing <i className="ri-arrow-right-line text-base"></i>
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
